/* Security page — Fail2Ban blocked IPs + WebRTC settings + IP whitelist info */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, Lock, LockOpen, Globe, WaveSine, FloppyDisk } from '@phosphor-icons/react';

const Security = () => {
    const [stats, setStats] = useState(null);
    const [webrtc, setWebrtc] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadAll = () => {
        api.get('/security/login-protection').then((r) => setStats(r.data));
        api.get('/webrtc/settings').then((r) => setWebrtc(r.data)).catch(() => {});
    };

    useEffect(() => {
        loadAll();
        const t = setInterval(() => api.get('/security/login-protection').then((r) => setStats(r.data)), 5000);
        return () => clearInterval(t);
    }, []);

    const unblock = async (ip) => {
        try {
            await api.post(`/security/unblock/${ip}`);
            toast.success(`IP ${ip} odblokována`);
            loadAll();
        } catch (e) {
            toast.error('Nelze odblokovat: ' + (e.response?.data?.detail || e.message));
        }
    };

    const saveWebRtc = async () => {
        setSaving(true);
        try {
            const payload = {
                wss_url: webrtc.wss_url || null,
                sip_domain: webrtc.sip_domain || null,
                stun_servers: typeof webrtc.stun_servers === 'string'
                    ? webrtc.stun_servers.split(/\s+/).filter(Boolean)
                    : webrtc.stun_servers,
                turn_url: webrtc.turn_url || null,
                turn_username: webrtc.turn_username || null,
                turn_credential: webrtc._turn_cred_input || undefined,
                enable_video: !!webrtc.enable_video,
                auto_answer: !!webrtc.auto_answer,
                ringtone_enabled: webrtc.ringtone_enabled !== false,
            };
            const r = await api.put('/webrtc/settings', payload);
            setWebrtc(r.data);
            toast.success('WebRTC nastavení uloženo');
        } catch (e) {
            toast.error('Uložení selhalo: ' + (e.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="security-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <ShieldCheck size={28} style={{ color: 'var(--brand)' }} />
                    Zabezpečení
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Fail2Ban ochrana, IP whitelist (WireGuard VPN), WebRTC konfigurace
                </p>
            </div>

            {/* Fail2Ban */}
            <div className="rounded-xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="flex items-center gap-2 mb-4">
                    <Lock size={18} style={{ color: 'var(--brand)' }} />
                    <h2 className="text-lg font-bold">Brute-force ochrana</h2>
                </div>
                {stats ? (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-xs">
                            <div><div style={{ color: 'var(--text-secondary)' }}>Limit pokusů</div>
                                <div className="text-xl font-mono font-bold" data-testid="sec-fail-limit">
                                    {stats.config.fail_limit}
                                </div></div>
                            <div><div style={{ color: 'var(--text-secondary)' }}>Okno (s)</div>
                                <div className="text-xl font-mono font-bold">{stats.config.window_seconds}</div></div>
                            <div><div style={{ color: 'var(--text-secondary)' }}>Blokace (s)</div>
                                <div className="text-xl font-mono font-bold">{stats.config.block_duration_seconds}</div></div>
                            <div><div style={{ color: 'var(--text-secondary)' }}>Sledované IP</div>
                                <div className="text-xl font-mono font-bold">{stats.currently_tracked_ips}</div></div>
                        </div>

                        <div className="text-xs uppercase tracking-[0.15em] font-bold mb-2"
                             style={{ color: 'var(--text-secondary)' }}>
                            Aktuálně zablokované IP ({Object.keys(stats.currently_blocked || {}).length})
                        </div>
                        {Object.keys(stats.currently_blocked || {}).length === 0 ? (
                            <div className="text-sm py-3" style={{ color: 'var(--text-disabled)' }}
                                 data-testid="sec-no-blocked">
                                Žádné aktivní blokace.
                            </div>
                        ) : (
                            <table className="w-full text-sm" data-testid="sec-blocked-table">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-wider"
                                        style={{ color: 'var(--text-disabled)' }}>
                                        <th className="py-2">IP</th>
                                        <th className="py-2">Zbývá (s)</th>
                                        <th className="py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                {Object.entries(stats.currently_blocked).map(([ip, sec]) => (
                                    <tr key={ip} className="border-t"
                                        style={{ borderColor: 'var(--border-default)' }}>
                                        <td className="py-2 font-mono">{ip}</td>
                                        <td className="py-2 font-mono">{sec}</td>
                                        <td className="py-2 text-right">
                                            <button
                                                onClick={() => unblock(ip)}
                                                data-testid={`sec-unblock-${ip}`}
                                                className="px-3 py-1 rounded text-xs border
                                                          hover:bg-[var(--bg-surface-hover)]"
                                                style={{ borderColor: 'var(--border-default)' }}
                                            >
                                                <LockOpen size={12} className="inline mr-1" /> Odblokovat
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </>
                ) : <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Načítám…</div>}
            </div>

            {/* IP Whitelist */}
            <div className="rounded-xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="flex items-center gap-2 mb-3">
                    <Globe size={18} style={{ color: 'var(--brand)' }} />
                    <h2 className="text-lg font-bold">IP Whitelist (admin endpointy)</h2>
                </div>
                <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                    Konfigurováno přes <code>ADMIN_IP_WHITELIST</code> v <code>/app/backend/.env</code>.
                    Pro WireGuard VPN nastavte: <code className="text-[var(--brand)]">ADMIN_IP_WHITELIST=10.8.0.0/24</code>
                </p>
                <div className="flex flex-wrap gap-2" data-testid="sec-whitelist-list">
                    {(stats?.ip_whitelist || []).length === 0 ? (
                        <span className="text-xs px-3 py-1 rounded-full"
                              style={{ background: 'var(--bg-surface-hover)', color: 'var(--text-disabled)' }}>
                            Žádný whitelist (admin endpointy otevřené)
                        </span>
                    ) : (stats.ip_whitelist || []).map((c) => (
                        <span key={c} className="text-xs font-mono px-3 py-1 rounded-full"
                              style={{ background: 'rgba(163,230,53,0.15)', color: 'var(--brand)' }}>
                            {c}
                        </span>
                    ))}
                </div>
            </div>

            {/* WebRTC */}
            {webrtc && (
                <div className="rounded-xl border p-6"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center gap-2 mb-4">
                        <WaveSine size={18} style={{ color: 'var(--brand)' }} />
                        <h2 className="text-lg font-bold">WebRTC Softphone nastavení</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <label className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>WSS URL Asterisku</div>
                            <input
                                data-testid="sec-wss-input"
                                value={webrtc.wss_url || ''}
                                onChange={(e) => setWebrtc({ ...webrtc, wss_url: e.target.value })}
                                placeholder="wss://pbx.solar-green.cz:8089/ws"
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>SIP doména (realm)</div>
                            <input
                                data-testid="sec-sipdom-input"
                                value={webrtc.sip_domain || ''}
                                onChange={(e) => setWebrtc({ ...webrtc, sip_domain: e.target.value })}
                                placeholder="pbx.solar-green.cz"
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs col-span-full">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                                STUN servery (oddělené mezerou)
                            </div>
                            <input
                                value={Array.isArray(webrtc.stun_servers) ? webrtc.stun_servers.join(' ') : (webrtc.stun_servers || '')}
                                onChange={(e) => setWebrtc({ ...webrtc, stun_servers: e.target.value })}
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>TURN URL (volitelné)</div>
                            <input
                                value={webrtc.turn_url || ''}
                                onChange={(e) => setWebrtc({ ...webrtc, turn_url: e.target.value })}
                                placeholder="turn:turn.solar-green.cz:3478"
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>TURN username</div>
                            <input
                                value={webrtc.turn_username || ''}
                                onChange={(e) => setWebrtc({ ...webrtc, turn_username: e.target.value })}
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                                TURN credential {webrtc.turn_credential_set && <span className="text-[var(--brand)]">(nastaveno)</span>}
                            </div>
                            <input
                                type="password"
                                placeholder={webrtc.turn_credential_set ? '••••••' : ''}
                                onChange={(e) => setWebrtc({ ...webrtc, _turn_cred_input: e.target.value })}
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                        <label className="text-xs flex items-center gap-2 mt-2">
                            <input
                                type="checkbox"
                                checked={!!webrtc.enable_video}
                                onChange={(e) => setWebrtc({ ...webrtc, enable_video: e.target.checked })}
                            />
                            Povolit video hovory
                        </label>
                        <label className="text-xs flex items-center gap-2 mt-2">
                            <input
                                type="checkbox"
                                checked={webrtc.ringtone_enabled !== false}
                                onChange={(e) => setWebrtc({ ...webrtc, ringtone_enabled: e.target.checked })}
                            />
                            Vyzváněcí tón
                        </label>
                    </div>
                    <button
                        data-testid="sec-webrtc-save"
                        onClick={saveWebRtc}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg font-bold text-sm disabled:opacity-50"
                        style={{ background: 'var(--brand)', color: '#000' }}
                    >
                        <FloppyDisk size={14} className="inline mr-2" />
                        {saving ? 'Ukládám…' : 'Uložit WebRTC'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default Security;
